import React from 'react';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { isLightColor } from '../../utils/colorContrast';

// Version hash to ensure proper cache busting
const BUTTON_VERSION = 'v1.6.0';

// Styled button that matches the original design specs
const StyledButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.isCompact ? '4px 12px' : '8px 12px 8px 16px'};
  gap: 8px;
  height: ${props => props.isCompact ? '32px' : '40px'};
  min-width: ${props => props.isCompact ? '100px' : '128px'};
  border: none;
  background: ${props =>
    props.explicitAccent && props.customColor
      ? `${props.customColor}27`
      : props.isSecondary
        ? `${props.themeSecondary}27`
        : props.customColor
          ? `${props.customColor}27`
          : '#056bfa27'};
  border-radius: 20px;
  cursor: pointer;
  text-transform: none; // Prevent uppercase transformation
  box-shadow: none;
  transition: background-color 0.3s ease;
  font-family: inherit;
  
  .label {
    margin-top: 1px;
    font-size: ${props => props.isCompact ? '14px' : '16px'};
    line-height: ${props => props.isCompact ? '16px' : '20px'};
    color: ${props =>
      props.explicitAccent && props.customColor
        ? props.customColor
        : props.isSecondary
          ? props.themeSecondary
          : props.customColor === '#13FFA3'
            ? '#000000'
            : props.isLightSecondary
              ? '#333333'
              : props.themeSecondary};
    letter-spacing: ${props => props.isCompact ? '0.5px' : '1px'};
    font-weight: normal;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  &:hover {
    background: ${props =>
      props.explicitAccent && props.customColor
        ? `${props.customColor}49`
        : props.isSecondary
          ? `${props.themeSecondary}49`
          : props.customColor
            ? `${props.customColor}49`
            : '#056bfa49'};
    box-shadow: none;
  }
  
  &:disabled {
    background: ${props => props.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
    color: ${props => props.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
    cursor: not-allowed;
  }
  
  /* Outlined variant styling */
  ${props => props.variant === 'outlined' && `
    border: ${props =>
      props.explicitAccent && props.customColor
        ? `1px solid ${props.customColor}`
        : props.isSecondary
          ? `1px solid ${props.themeSecondary}`
          : `1px solid ${props.customColor || '#056dfa'}`};
    background: transparent;
    
    &:hover {
      background: ${props =>
        props.explicitAccent && props.customColor
          ? `${props.customColor}10`
          : props.isSecondary
            ? `${props.themeSecondary}10`
            : (props.customColor ? `${props.customColor}10` : '#056bfa10')};
    }
    
    .label {
      color: ${props =>
        props.explicitAccent && props.customColor
          ? props.customColor
          : props.isSecondary
            ? props.themeSecondary
            : (props.customColor || props.themeSecondary)};
    }
  `}
  
  /* Special styling for mint green buttons */
  ${props => props.customColor === '#13FFA3' && `
    .label {
      color: #000000;
    }
  `}
  
  /* Focus styles for accessibility */
  &:focus {
    outline: 2px solid ${props => props.customColor || '#056dfa'};
    outline-offset: 2px;
  }
  
  /* Active state */
  &:active {
    transform: translateY(1px);
  }
`;

/**
 * NewCustomButton - A reusable button component styled according to design specs
 * Completely independent of MUI Button to eliminate refresh styling issues
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Button text/content
 * @param {string} props.color - Custom color (hex code) to override default blue
 * @param {React.ReactNode} props.startIcon - Icon to display before text
 * @param {React.ReactNode} props.endIcon - Icon to display after text
 * @param {Function} props.onClick - Click handler function
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {boolean} props.loading - Shows a spinner and disables the button
 * @param {Object} props.sx - Additional styles to apply (merged with existing styles)
 * @param {string} props.variant - Button variant ('contained', 'outlined', 'text', 'secondary')
 * @param {string} props.size - Button size ('small', 'medium', 'large')
 * @param {boolean} props.fullWidth - Whether button should take full width
 * @param {string} props.type - Button type ('button', 'submit', 'reset')
 * @param {boolean} props.compact - Whether to use compact styling (smaller height and font)
 * @param {boolean} props.useSecondary - Whether to use secondary color instead of primary
 */
const NewCustomButton = React.forwardRef(({ 
  children, 
  color, 
  startIcon, 
  endIcon,
  onClick, 
  disabled = false,
  loading = false,
  sx = {},
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
  type = 'button',
  compact = false,
  useSecondary = false,
  ...props 
}, ref) => {
  const { isDarkMode, primaryColor, secondaryColor } = useTheme();
  
  // Check if we should use secondary color (either by variant or useSecondary prop)
  const isSecondary = useSecondary || variant === 'secondary';
  
  const buttonColor = color || primaryColor || '#056dfa';
  const explicitAccent = Boolean(color);
  
  // Check if secondary color is too light for text
  const isLightSecondary = isLightColor(secondaryColor);
  
  // Determine if button should be compact
  const isCompact = compact || size === 'small';

  const spinnerColor = (() => {
    if (explicitAccent && color) return color;
    if (isSecondary) return secondaryColor;
    return buttonColor;
  })();
  
  // Merge custom styles with sx prop
  const customStyles = {
    ...(fullWidth && { width: '100%' }),
    ...sx
  };
  
  return (
    <StyledButton
      ref={ref}
      onClick={onClick}
      disabled={disabled || loading}
      type={type}
      customColor={buttonColor}
      themeSecondary={secondaryColor}
      explicitAccent={explicitAccent}
      isSecondary={isSecondary}
      isDarkMode={isDarkMode}
      isLightSecondary={isLightSecondary}
      isCompact={isCompact}
      variant={variant}
      style={customStyles}
      data-button-version={BUTTON_VERSION}
      {...props}
    >
      {loading ? (
        <CircularProgress
          size={isCompact ? 12 : 16}
          thickness={5}
          sx={{ color: spinnerColor, flexShrink: 0 }}
        />
      ) : (
        startIcon && <span className="start-icon">{startIcon}</span>
      )}
      <span className="label">{children}</span>
      {!loading && endIcon && <span className="end-icon">{endIcon}</span>}
    </StyledButton>
  );
});

// Set display name for better debugging
NewCustomButton.displayName = 'NewCustomButton';

export default NewCustomButton;
